import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { adminApi, mediaUrl } from "../api";
import { AField, Btn, Modal, PageHead, Toggle, Uploader, inputCls } from "../components";

const EMPTY = { name: "", description: "", price: 0, graphic: "", coming_soon: true, featured: false, active: true };

export default function ShopAdmin() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/shop").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openEdit = (s) => {
    setForm({ name: s.name, description: s.description || "", price: s.price ?? 0, graphic: s.graphic || "", coming_soon: !!s.coming_soon, featured: !!s.featured, active: !!s.active });
    setEditing(s.id);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, price: Number(form.price) || 0 };
      if (editing === "new") await adminApi.post("/admin/shop", payload);
      else await adminApi.put(`/admin/shop/${editing}`, payload);
      setEditing(null);
      load();
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    await adminApi.delete(`/admin/shop/${s.id}`);
    load();
  };

  return (
    <div data-testid="admin-shop">
      <PageHead title="COBAIN CULTURE" sub="Coming Soon teasers for the storefront. Purchasing stays off until you flip the switch.">
        <Btn onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="shop-new">
          <Plus size={13} /> NEW TEASER
        </Btn>
      </PageHead>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shop-list">
        {items.map((s, i) => (
          <div key={s.id} className={`border p-5 ${s.active ? "border-tan/30 bg-card/40" : "border-woodborder bg-card/20 opacity-70"}`} data-testid={`shop-row-${i}`}>
            {s.graphic && <img src={mediaUrl(s.graphic)} alt="" className="w-full aspect-square object-cover border border-woodborder mb-4" />}
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-editorial font-bold text-cream">{s.name}</p>
              {s.coming_soon && <span className="font-mono text-[9px] tracking-[0.2em] bg-gold text-obsidian font-bold px-2.5 py-1 rounded-full">COMING SOON</span>}
              {!s.active && <span className="font-mono text-[9px] tracking-[0.2em] text-clay border border-clay/40 px-2.5 py-1 rounded-full">HIDDEN</span>}
            </div>
            <p className="mt-1.5 text-clay text-xs font-light line-clamp-2">{s.description}</p>
            <p className="mt-2 font-mono text-[10px] tracking-[0.15em] text-gold-bright">PRICE: {s.price ? `$${s.price}` : "NOT SET"}</p>
            <div className="mt-4 flex gap-2">
              <Btn variant="ghost" onClick={() => openEdit(s)} data-testid={`shop-edit-${i}`}><Pencil size={12} /> EDIT</Btn>
              <Btn variant="danger" onClick={() => remove(s)} data-testid={`shop-delete-${i}`}><Trash2 size={12} /></Btn>
            </div>
          </div>
        ))}
      </div>

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing === "new" ? "NEW TEASER" : "EDIT TEASER"}>
        <div className="space-y-5" data-testid="shop-editor">
          <AField label="COMING SOON GRAPHIC">
            <div className="flex items-start gap-4 flex-wrap">
              <Uploader onUploaded={(p) => setForm({ ...form, graphic: p })} accept="image/*" label="UPLOAD GRAPHIC" testId="shop-graphic-upload" />
              {form.graphic && <img src={mediaUrl(form.graphic)} alt="" className="w-24 h-24 object-cover border border-woodborder" data-testid="shop-graphic-preview" />}
            </div>
          </AField>
          <AField label="TEASER NAME *"><input className={inputCls} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="shop-name" /></AField>
          <AField label="DESCRIPTION"><textarea className={`${inputCls} min-h-[80px] resize-y`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="shop-description" /></AField>
          <AField label="PRICE (STAYS BLURRED WHILE COMING SOON)"><input type="number" step="0.01" className={inputCls} value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="shop-price" /></AField>
          <div className="flex flex-wrap gap-8">
            <Toggle checked={form.coming_soon} onChange={(v) => setForm({ ...form, coming_soon: v })} label="COMING SOON MODE" testId="shop-coming-soon" />
            <Toggle checked={form.featured} onChange={(v) => setForm({ ...form, featured: v })} label="FEATURED" testId="shop-featured" />
            <Toggle checked={form.active} onChange={(v) => setForm({ ...form, active: v })} label="ACTIVE ON SITE" testId="shop-active" />
          </div>
          <div className="flex gap-3 pt-2 border-t border-woodborder">
            <Btn variant="gold" disabled={saving || !form.name.trim()} onClick={save} data-testid="shop-save">{saving ? "SAVING..." : "SAVE TEASER"}</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}
